import React from 'react';
import { RelBox } from '../../../api/ai.api.types';

interface FocusBlurProps {
    box: RelBox | null;
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

// Focus effect: everything OUTSIDE the (tracked) meter box is blurred and slightly dimmed, so
// the meter reads as "in focus". Built from four backdrop-filter panels around the box — a
// clip-path hole would be a single element but cannot animate as smoothly across browsers.
// Renders nothing while no meter is tracked (blurring the whole image would hinder searching).
const FocusBlur = ({ box }: FocusBlurProps) => {
    if (!box) {
        return null;
    }

    const x0 = clamp01(box.x);
    const y0 = clamp01(box.y);
    const x1 = clamp01(box.x + box.w);
    const y1 = clamp01(box.y + box.h);

    const panelStyle: React.CSSProperties = {
        position: 'absolute',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        backgroundColor: 'rgba(0, 0, 0, 0.25)',
        transition: 'all 0.15s linear',
    };

    return (
        <div data-testid="focus-blur" className={'absolute inset-0 overflow-hidden pointer-events-none'} aria-hidden={true}>
            <div style={{ ...panelStyle, left: 0, top: 0, width: '100%', height: `${y0 * 100}%` }} />
            <div style={{ ...panelStyle, left: 0, top: `${y1 * 100}%`, width: '100%', height: `${(1 - y1) * 100}%` }} />
            <div style={{ ...panelStyle, left: 0, top: `${y0 * 100}%`, width: `${x0 * 100}%`, height: `${(y1 - y0) * 100}%` }} />
            <div style={{ ...panelStyle, left: `${x1 * 100}%`, top: `${y0 * 100}%`, width: `${(1 - x1) * 100}%`, height: `${(y1 - y0) * 100}%` }} />
        </div>
    );
};

export default FocusBlur;
